import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import useSWR from "swr";
import { toast } from "sonner";
import {
  Video,
  Clock,
  ChevronLeft,
  ChevronRight,
  ArrowRight,
  ArrowLeft,
  CalendarCheck,
  Check,
  CalendarPlus,
  Globe,
} from "lucide-react";
import { SiteLayout } from "@/components/layout/SiteLayout";
import { PageHero } from "@/components/ui";
import { fetchConsultationConfig, fetchPrograms, submitConsultation } from "@/lib/api";
import { formInput, formSelect, formTextarea, formLabel, btnNavy } from "@/lib/form-styles";
import { showPhoneValidationError, validatePhoneWithCountryCode } from "@/lib/phoneValidation";
import { HearAboutUsField } from "@/components/HearAboutUsField";
import { Seo } from "@/lib/seo/Seo";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const TIMEZONES = [
  { value: "Asia/Kolkata", label: "India Standard Time (IST +5:30)" },
  { value: "Asia/Dubai", label: "Gulf Standard Time (GST +4)" },
  { value: "Europe/London", label: "British Time (GMT +0 / BST)" },
  { value: "America/New_York", label: "US Eastern (EST -5 / EDT)" },
  { value: "Asia/Singapore", label: "Singapore Time (SGT +8)" },
];

const COUNTRIES = [
  "India", "United Arab Emirates", "Saudi Arabia", "Qatar", "Kuwait", "Bahrain", "Oman",
  "United Kingdom", "United States", "Singapore", "Hong Kong", "Other",
];

const FAQ = [
  {
    q: "Is the consultation free?",
    a: "Yes, 100% free with zero obligation. Our India advisory team will help you choose programmes, dates, and next steps.",
  },
  {
    q: "Who should join the call?",
    a: "We encourage both parents and students to join the consultation, ask questions, explore available opportunities and gain clarity on the most suitable learning experience.",
  },
  {
    q: "Can I reschedule later?",
    a: "Yes — reply to your confirmation email or contact our Gurgaon team and we will find another slot that works for you.",
  },
];

const WEEKDAY_KEYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

function weekdayKeyForDate(year, month, day) {
  return WEEKDAY_KEYS[new Date(year, month, day).getDay()];
}

function getTomorrowStart() {
  const tomorrow = new Date();
  tomorrow.setHours(0, 0, 0, 0);
  tomorrow.setDate(tomorrow.getDate() + 1);
  return tomorrow;
}

function isDaySelectable(year, month, day, availableWeekdays) {
  const date = new Date(year, month, day);
  date.setHours(0, 0, 0, 0);
  if (date < getTomorrowStart()) return false;
  if (!availableWeekdays?.length) return false;
  return availableWeekdays.includes(weekdayKeyForDate(year, month, day));
}

export default function Consultation() {
  const [searchParams] = useSearchParams();
  const programFromUrl = searchParams.get("program") || "";

  const { data: config } = useSWR("consultation-config", fetchConsultationConfig);
  const { data: programs = [] } = useSWR("india-programs-consult", fetchPrograms);

  const availableWeekdays = config?.weekdays ?? ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
  const staticSlots = config?.slots ?? [];

  const [currentDate, setCurrentDate] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d;
  });
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedSlotKey, setSelectedSlotKey] = useState(null);
  const [timezone, setTimezone] = useState(config?.timezone_default || "Asia/Kolkata");
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    country: "India",
    student_age: "",
    program_interest: programFromUrl,
    message: "",
    how_did_you_hear: "",
  });

  useEffect(() => {
    if (programFromUrl) {
      setForm((f) => ({ ...f, program_interest: programFromUrl }));
    }
  }, [programFromUrl]);

  const timezoneLabel = TIMEZONES.find((t) => t.value === timezone)?.label || timezone;

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDayIndex = new Date(year, month, 1).getDay();
  const shiftedFirstDay = firstDayIndex === 0 ? 6 : firstDayIndex - 1;
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const consultationDate = selectedDay
    ? `${year}-${String(month + 1).padStart(2, "0")}-${String(selectedDay).padStart(2, "0")}`
    : null;

  const availableSlots = selectedDay ? staticSlots : [];

  const selectedSlotLabel = availableSlots.find((s) => s.key === selectedSlotKey)?.label ?? null;

  const slotSummary =
    selectedDay && selectedSlotLabel
      ? `${MONTH_NAMES[month]} ${selectedDay}, ${year} @ ${selectedSlotLabel}`
      : "";

  const changeMonth = (delta) => {
    setCurrentDate((d) => new Date(d.getFullYear(), d.getMonth() + delta, 1));
    setSelectedDay(null);
    setSelectedSlotKey(null);
  };

  const selectDate = (day) => {
    setSelectedDay(day);
    setSelectedSlotKey(null);
  };

  const goToStep2 = () => {
    if (!selectedDay || !selectedSlotKey || !selectedSlotLabel) return;
    setStep(2);
  };

  const submit = async (e) => {
    e.preventDefault();
    const phoneCheck = validatePhoneWithCountryCode(form.phone, { required: true });
    if (!phoneCheck.ok) {
      showPhoneValidationError(phoneCheck.message, toast);
      return;
    }
    if (!form.how_did_you_hear) {
      toast.error("Please tell us how you heard about us.");
      return;
    }
    setSubmitting(true);
    try {
      await submitConsultation({
        name: form.name,
        email: form.email,
        phone: phoneCheck.value,
        country: form.country || "India",
        student_age: form.student_age,
        program_interest: form.program_interest,
        consultation_date: consultationDate,
        consultation_time: selectedSlotLabel,
        consultation_time_slot: selectedSlotKey,
        timezone: timezoneLabel,
        message: form.message || undefined,
        how_did_you_hear: form.how_did_you_hear,
        form_url: window.location.href,
      });
      setConfirmed({
        name: form.name,
        datetime: slotSummary,
        timezone: timezoneLabel,
      });
      setStep(3);
      toast.success("Consultation successfully booked!");
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.response?.data?.error || "Could not submit your booking. Please try again or contact us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const programOptions = useMemo(() => {
    const opts = [{ value: "", label: "Undecided / Help me choose" }];
    programs.forEach((p) => opts.push({ value: p.title, label: p.title }));
    return opts;
  }, [programs]);

  return (
    <SiteLayout>
      <Seo
        title="Book a Free Consultation | A Learning Lab"
        description="Book a free consultation with the A Learning Lab advisory team. Choose a date and time to discuss virtual internships and programmes for your child."
        path="/consultation"
      />
      <div data-testid="book-consultation-page" className="page-main">
        <PageHero
          testId="consultation-hero"
          icon={Video}
          eyebrow="Book a Complimentary Consultation"
          title="We're Here to Help"
          intro="Get personalized guidance to explore the right learning experiences for your child and take the next step with clarity."
        />

        <section className="py-12 sm:py-16 lg:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-3xl border border-brand-200 shadow-2xl overflow-hidden grid lg:grid-cols-12 min-h-[640px]">
              <div className="lg:col-span-4 bg-brand-50 border-b lg:border-b-0 lg:border-r border-brand-200 p-6 sm:p-8 flex flex-col justify-between">
                <div>
                  <div className="pb-6 border-b border-brand-200">
                    <h3 className="font-sans font-bold text-ink text-base">A Learning Lab</h3>
                  </div>

                  <div className="mt-6 space-y-4">
                    <h2 className="font-sans text-xl font-bold text-ink">Explore Your Options</h2>
                    {[
                      { icon: Clock, text: "15-minute one-to-one consultation" },
                      { icon: Video, text: "Online via Google Meet or Zoom" },
                    ].map(({ icon: Icon, text }) => (
                      <div key={text} className="flex items-center gap-3 text-xs text-ink/80 font-medium">
                        <div className="w-7 h-7 rounded-lg bg-brand-100 flex items-center justify-center shrink-0">
                          <Icon className="w-4 h-4 text-brand-600" />
                        </div>
                        <span>{text}</span>
                      </div>
                    ))}
                  </div>

                  <div className="mt-8 bg-white p-4 rounded-xl border border-brand-200 text-xs text-ink/70 space-y-2">
                    <p className="font-bold text-ink uppercase text-[10px] tracking-wider">What we can help you with:</p>
                    <ul className="space-y-1.5 list-disc list-inside">
                      <li>Explore programmes aligned with your interests and aspirations.</li>
                      <li>Understand the available learning formats and experiences.</li>
                      <li>Discover opportunities to build practical skills and explore future pathways.</li>
                      <li>Get clarity on enrolment, fees and the next steps.</li>
                    </ul>
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-brand-200 text-xs text-ink/60 italic">
                  <h2 className="font-sans text-xl font-bold text-ink not-italic">Find Your Fit</h2>
                  <p>Explore your interests. Compare programmes. Choose the experience that feels right for you.</p>
                </div>
              </div>

              <div className="lg:col-span-8 p-6 sm:p-8 flex flex-col justify-between">
                {step === 1 && (
                  <div className="space-y-6" data-testid="consultation-step-1">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-brand-200">
                      <div>
                        <span className="text-[10px] font-bold text-brand-600 uppercase tracking-wider block">Step 1 of 2</span>
                        <h3 className="font-sans font-bold text-xl text-ink">Select a Date &amp; Time</h3>
                        <p className="text-xs text-ink/60 mt-1">Pick a slot with our advisory team.</p>
                      </div>
                      <div className="flex items-center gap-2 bg-brand-50 border border-brand-200 rounded-lg px-3 py-1.5 text-xs text-ink">
                        <Globe className="w-3.5 h-3.5 text-brand-600 shrink-0" />
                        <select
                          value={timezone}
                          onChange={(e) => {
                            setTimezone(e.target.value);
                            toast.success(`Timezone set to ${TIMEZONES.find((t) => t.value === e.target.value)?.label}`);
                          }}
                          className="bg-transparent font-semibold focus:outline-none cursor-pointer text-xs"
                        >
                          {TIMEZONES.map((t) => (
                            <option key={t.value} value={t.value}>
                              {t.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="grid md:grid-cols-12 gap-6">
                      <div className="md:col-span-7">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="font-sans font-bold text-ink text-sm">
                            {MONTH_NAMES[month]} {year}
                          </h4>
                          <div className="flex gap-1">
                            <button type="button" onClick={() => changeMonth(-1)} className="p-1.5 rounded-md border border-brand-200 hover:bg-brand-50 text-ink transition-colors">
                              <ChevronLeft className="w-4 h-4" />
                            </button>
                            <button type="button" onClick={() => changeMonth(1)} className="p-1.5 rounded-md border border-brand-200 hover:bg-brand-50 text-ink transition-colors">
                              <ChevronRight className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-bold text-ink/60 mb-2">
                          {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
                            <div key={`${d}-${i}`}>{d}</div>
                          ))}
                        </div>
                        <div className="grid grid-cols-7 gap-1 text-center text-xs">
                          {Array.from({ length: shiftedFirstDay }).map((_, i) => (
                            <div key={`pad-${i}`} className="py-2.5" />
                          ))}
                          {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => {
                            const selectable = isDaySelectable(year, month, day, availableWeekdays);
                            const active = selectedDay === day;
                            return (
                              <button
                                key={day}
                                type="button"
                                disabled={!selectable}
                                onClick={() => selectable && selectDate(day)}
                                className={`py-2.5 rounded-lg font-semibold transition-all ${
                                  active
                                    ? "bg-brand-500 text-white shadow-md scale-105"
                                    : selectable
                                      ? "bg-brand-50 text-ink hover:bg-brand-100 hover:text-ink border border-brand-300"
                                      : "text-ink/60 cursor-not-allowed"
                                }`}
                              >
                                {day}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      <div className="md:col-span-5 bg-brand-50 p-4 rounded-2xl border border-brand-200 flex flex-col justify-between">
                        <div>
                          <div className="flex items-center justify-between mb-3 border-b border-brand-200 pb-2">
                            <span className="text-xs font-bold text-ink">
                              {selectedDay ? `${MONTH_NAMES[month]} ${selectedDay}` : "Select a Date"}
                            </span>
                            <span className="text-[10px] text-ink/60 font-medium">
                              {selectedDay ? `${availableSlots.length} slots open` : "No date selected"}
                            </span>
                          </div>
                          <div className="space-y-2 max-h-[260px] overflow-y-auto pr-1">
                            {!selectedDay ? (
                              <p className="text-xs text-ink/60 text-center py-8 italic">
                                Please click an active date on the calendar to view available times.
                              </p>
                            ) : availableSlots.length === 0 ? (
                              <p className="text-xs text-ink/60 text-center py-8 italic">No times configured. Please contact us.</p>
                            ) : (
                              availableSlots.map((slot) => {
                                const active = selectedSlotKey === slot.key;
                                return (
                                  <button
                                    key={slot.key}
                                    type="button"
                                    onClick={() => setSelectedSlotKey(slot.key)}
                                    className={`w-full text-xs py-2.5 px-3 rounded-xl border transition-all font-bold flex items-center justify-between ${
                                      active
                                        ? "bg-brand-500 border-brand-500 text-white shadow-md"
                                        : "bg-white border-brand-200 text-ink hover:border-brand-500 hover:bg-brand-50"
                                    }`}
                                  >
                                    <span>{slot.label}</span>
                                    <span className={`font-medium text-[10px] ${active ? "text-white" : "text-brand-600"}`}>
                                      {active ? "Selected ✓" : "Confirm →"}
                                    </span>
                                  </button>
                                );
                              })
                            )}
                          </div>
                        </div>
                        <div className="pt-4 border-t border-brand-200 mt-4">
                          <button
                            type="button"
                            disabled={!selectedDay || !selectedSlotKey}
                            onClick={goToStep2}
                            data-testid="consultation-next-btn"
                            className={`w-full text-white text-xs font-bold py-3 rounded-xl transition-all flex items-center justify-center gap-2 ${
                              selectedDay && selectedSlotKey
                                ? "bg-sun-500 hover:bg-sun-400 shadow-md"
                                : "bg-brand-200 cursor-not-allowed"
                            }`}
                          >
                            <span>Next: Enter Details</span>
                            <ArrowRight className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div className="space-y-6" data-testid="consultation-step-2">
                    <div className="flex items-center justify-between pb-4 border-b border-brand-200">
                      <div>
                        <button type="button" onClick={() => setStep(1)} className="text-xs font-semibold text-brand-600 hover:underline inline-flex items-center gap-1 mb-1">
                          <ArrowLeft className="w-3.5 h-3.5" /> Back to date selection
                        </button>
                        <h3 className="font-sans font-bold text-xl text-ink">Enter Your Details</h3>
                      </div>
                      <span className="text-[10px] font-bold text-brand-600 uppercase tracking-wider">Step 2 of 2</span>
                    </div>

                    <div className="bg-brand-50 border border-brand-300 rounded-xl p-3.5 flex items-center justify-between text-xs text-ink gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <CalendarCheck className="w-5 h-5 text-brand-600 shrink-0" />
                        <div className="min-w-0">
                          <span className="font-bold block truncate">{slotSummary}</span>
                          <span className="text-[11px] text-ink/70">Timezone: {timezoneLabel} • 15 Mins</span>
                        </div>
                      </div>
                      <button type="button" onClick={() => setStep(1)} className="text-[11px] font-bold text-brand-600 hover:underline shrink-0">
                        Change
                      </button>
                    </div>

                    <form onSubmit={submit} className="space-y-4">
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="consult-name" className={formLabel}>
                            Parent / Guardian Full Name *
                          </label>
                          <input id="consult-name" required placeholder="e.g. Priya Sharma" className={formInput} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                        </div>
                        <div>
                          <label htmlFor="consult-email" className={formLabel}>
                            Email Address *
                          </label>
                          <input id="consult-email" type="email" required placeholder="you@example.com" className={formInput} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                        </div>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="consult-phone" className={formLabel}>
                            Phone Number (with country code) *
                          </label>
                          <input id="consult-phone" type="tel" required placeholder="+91 88742 00200" className={formInput} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                        </div>
                        <div>
                          <label htmlFor="consult-country" className={formLabel}>
                            Country of Residence *
                          </label>
                          <select id="consult-country" required className={formSelect} value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })}>
                            <option value="">Select country...</option>
                            {COUNTRIES.map((c) => (
                              <option key={c} value={c}>
                                {c}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="consult-age" className={formLabel}>
                            Age
                          </label>
                          <input
                            id="consult-age"
                            type="text"
                            inputMode="numeric"
                            placeholder="e.g. 15"
                            className={formInput}
                            value={form.student_age}
                            onChange={(e) => setForm({ ...form, student_age: e.target.value })}
                          />
                        </div>
                        <div>
                          <label htmlFor="consult-program" className={formLabel}>
                            Programme interest
                          </label>
                          <select id="consult-program" className={formSelect} value={form.program_interest} onChange={(e) => setForm({ ...form, program_interest: e.target.value })}>
                            {programOptions.map((o) => (
                              <option key={o.label} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div>
                        <label htmlFor="consult-message" className={formLabel}>
                          Specific questions or topics you wish to discuss
                        </label>
                        <textarea
                          id="consult-message"
                          rows={3}
                          placeholder="Tell us about career interests, academic goals, or questions about bootcamps and virtual programmes..."
                          className={formTextarea}
                          value={form.message}
                          onChange={(e) => setForm({ ...form, message: e.target.value })}
                        />
                      </div>
                      <HearAboutUsField
                        testId="consult-hear-about"
                        required
                        value={form.how_did_you_hear}
                        onChange={(e) => setForm({ ...form, how_did_you_hear: e.target.value })}
                      />
                      <div className="pt-2">
                        <button type="submit" disabled={submitting} data-testid="consultation-submit" className="w-full bg-sun-500 hover:bg-sun-400 disabled:opacity-60 text-ink text-xs font-bold py-3.5 rounded-xl transition-all shadow-md">
                          {submitting ? "Confirming…" : "Confirm Free Advisory Booking"}
                        </button>
                        <p className="text-[10px] text-ink/60 text-center mt-2">
                          By booking, you will receive a confirmation email with meeting details.
                        </p>
                      </div>
                    </form>
                  </div>
                )}

                {step === 3 && confirmed && (
                  <div className="space-y-6 py-8 text-center my-auto" data-testid="consultation-step-3">
                    <div className="w-16 h-16 bg-brand-100 text-brand-600 rounded-full flex items-center justify-center mx-auto mb-4 border border-brand-300">
                      <Check className="w-8 h-8" />
                    </div>
                    <span className="text-xs font-bold text-brand-600 uppercase tracking-wider block">Booking Confirmed!</span>
                    <h3 className="font-sans font-bold text-2xl text-ink">Your Advisory Session is Scheduled</h3>
                    <div className="bg-brand-50 p-6 rounded-2xl border border-brand-200 max-w-md mx-auto text-left text-xs space-y-2.5">
                      <div className="flex justify-between border-b border-brand-200 pb-2 gap-4">
                        <span className="text-ink/60 font-medium shrink-0">Session:</span>
                        <span className="font-bold text-ink text-right">Explore Your Options (15 min)</span>
                      </div>
                      <div className="flex justify-between border-b border-brand-200 pb-2 gap-4">
                        <span className="text-ink/60 font-medium shrink-0">Date &amp; Time:</span>
                        <span className="font-bold text-brand-600 text-right">{confirmed.datetime}</span>
                      </div>
                      <div className="flex justify-between border-b border-brand-200 pb-2 gap-4">
                        <span className="text-ink/60 font-medium shrink-0">Attendee:</span>
                        <span className="font-bold text-ink text-right">{confirmed.name}</span>
                      </div>
                      <div className="flex justify-between gap-4">
                        <span className="text-ink/60 font-medium shrink-0">Meeting Link:</span>
                        <span className="font-semibold text-ink flex items-center gap-1">
                          <Video className="w-3.5 h-3.5 text-brand-600" /> Sent to email
                        </span>
                      </div>
                    </div>
                    <div className="pt-4 flex flex-wrap items-center justify-center gap-3">
                      <button type="button" onClick={() => toast.success("Calendar event added!")} className={`${btnNavy} !text-xs !py-2.5 !px-5`}>
                        <CalendarPlus className="w-4 h-4 text-brand-600" /> Add to Google Calendar
                      </button>
                      <Link to="/" className="inline-flex items-center gap-2 border border-brand-200 text-ink text-xs font-semibold px-5 py-2.5 rounded-lg hover:bg-brand-50 transition-colors">
                        Return to Homepage
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 sm:py-16 lg:py-20 bg-white border-t border-brand-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <span className="text-xs font-bold tracking-widest text-brand-600 uppercase">Frequently Asked Questions</span>
              <h2 className="font-sans text-3xl font-bold text-ink mt-1">Everything you need to know about the call</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {FAQ.map((item) => (
                <div key={item.q} className="bg-brand-50 p-6 rounded-2xl border border-brand-200">
                  <h3 className="font-sans font-bold text-base text-ink mb-2">{item.q}</h3>
                  <p className="text-xs text-ink/70 leading-relaxed">{item.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}
